import { useRef } from 'react';
import { formatCurrency, formatQuantity } from '../../lib/format';
import { investmentTypes } from '../../types';
import type { Position } from '../../lib/investments';

export interface PositionsOverviewProps {
  positions: Position[];
  /** Avisa que algum papel entrou pelo custo, por falta de cotação. */
  partialPricing?: boolean;
}

/**
 * Lista horizontal das posições abertas, no mesmo formato dos outros cards do
 * dashboard: arrastar para o lado, um cartão por papel.
 */
export function PositionsOverview({ positions, partialPricing }: PositionsOverviewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    dragging.current = true;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    startScroll.current = scrollRef.current?.scrollLeft ?? 0;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = startScroll.current - (x - startX.current);
  };

  const handleMouseUp = () => {
    dragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  if (positions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nenhuma posição em aberto. Cadastre uma compra na aba Investimentos.
      </p>
    );
  }

  const sorted = [...positions].sort(
    (a, b) => (b.marketValue ?? b.totalCost) - (a.marketValue ?? a.totalCost)
  );

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={scrollRef}
        className="flex w-full min-w-0 gap-2 overflow-x-auto sm:gap-3 [&::-webkit-scrollbar]:hidden"
        style={{ cursor: 'grab', scrollbarWidth: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {sorted.map((position) => {
          const value = position.marketValue ?? position.totalCost;
          const gain = position.unrealizedGain;
          const pct = position.unrealizedGainPct;
          const tone =
            gain === null ? 'text-muted-foreground' : gain >= 0 ? 'text-success' : 'text-destructive';

          return (
            <div
              key={position.asset.id}
              className="min-w-36 shrink-0 select-none rounded-md border border-border p-2 sm:min-w-44"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className="size-2 shrink-0 rounded-sm"
                  style={{ backgroundColor: investmentTypes[position.asset.type].color }}
                  aria-hidden
                />
                <span className="text-sm font-medium text-foreground">
                  {position.asset.ticker}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatQuantity(position.quantity)}
                </span>
              </div>

              <div className="mt-1.5 text-base font-semibold text-foreground">
                R$ {formatCurrency(value)}
              </div>

              <div className={`text-xs ${tone}`}>
                {gain === null ? (
                  'sem cotação'
                ) : (
                  <>
                    {gain >= 0 ? '+' : '-'}R$ {formatCurrency(Math.abs(gain))}
                    {pct !== null && (
                      <span className="ml-1">
                        ({pct >= 0 ? '+' : ''}
                        {pct.toFixed(1)}%)
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {partialPricing && (
        <p className="text-xs text-muted-foreground">
          Papéis sem cotação entram pelo custo. Use “Cotações” na aba Investimentos.
        </p>
      )}
    </div>
  );
}
